import { useState, useEffect } from 'react';
import { adminApi } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
} from '../components/ui/pagination';
import { formatCurrency, formatDateTime } from '../lib/utils';
import { toast } from 'sonner';
import { Loader2, Heart } from 'lucide-react';

export default function WishlistsPage() {
  const [wishlists, setWishlists] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalWishlists, setTotalWishlists] = useState(0);
  const pageSize = 30;

  useEffect(() => {
    fetchWishlists();
  }, [currentPage]);

  const fetchWishlists = async () => {
    setLoading(true);
    try {
      const res = await adminApi.getWishlists({
        limit: pageSize,
        skip: (currentPage - 1) * pageSize,
      });
      const filtered = res.data.filter((w) => w.items?.length > 0);
      setWishlists(filtered);
      if (filtered.length === pageSize) {
        setTotalWishlists(currentPage * pageSize + 1);
      } else {
        setTotalWishlists((currentPage - 1) * pageSize + filtered.length);
      }
    } catch (error) {
      toast.error('Failed to load wishlists');
    } finally {
      setLoading(false);
    }
  };

  const totalItems = wishlists.reduce((sum, w) => sum + (w.items?.length || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="wishlists-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl text-primary font-semibold">Wishlists</h1>
          <p className="text-muted-foreground mt-1">View customers' wishlisted items</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-muted-foreground">Active Wishlists</p>
          <p className="text-2xl font-heading font-bold text-primary">{wishlists.length}</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="card-base">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Wishlisted Items</p>
              <p className="text-2xl font-heading font-bold text-primary">{totalItems}</p>
            </div>
            <Heart className="w-8 h-8 text-red-500/20" />
          </CardContent>
        </Card>
        <Card className="card-base">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Avg Items per Wishlist</p>
              <p className="text-2xl font-heading font-bold text-primary">
                {wishlists.length > 0 ? (totalItems / wishlists.length).toFixed(1) : 0}
              </p>
            </div>
            <Heart className="w-8 h-8 text-primary/20" />
          </CardContent>
        </Card>
      </div>

      {/* Wishlists Table */}
      <Card className="card-base">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Customer</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Products</TableHead>
                  <TableHead>Total Value</TableHead>
                  <TableHead>Last Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {wishlists.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      No active wishlists
                    </TableCell>
                  </TableRow>
                ) : (
                  wishlists.map((wishlist) => (
                    <TableRow key={wishlist.id} className="hover:bg-canvas/50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                            <Heart className="w-5 h-5 text-red-500" />
                          </div>
                          <span className="font-medium text-primary">{wishlist.customer_name || 'Unknown'}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-red-100 text-red-800 border-red-200">
                          {wishlist.items?.length || 0} items
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1 max-w-[300px]">
                          {wishlist.items?.slice(0, 3).map((item, idx) => (
                            <p key={idx} className="text-xs text-muted-foreground truncate">
                              {item.product_name}
                            </p>
                          ))}
                          {wishlist.items?.length > 3 && (
                            <p className="text-xs text-primary">+{wishlist.items.length - 3} more</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-semibold">
                        {formatCurrency(wishlist.items?.reduce((sum, i) => sum + (i.price || 0), 0) || 0)}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDateTime(wishlist.updated_at)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {!loading && wishlists.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(currentPage - 1) * pageSize + 1}-
            {Math.min(currentPage * pageSize, totalWishlists)} of ~{totalWishlists}{" "}
            wishlists
          </p>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (currentPage > 1) setCurrentPage(currentPage - 1);
                  }}
                  className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink href="#" isActive={true}>
                  {currentPage}
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (wishlists.length === pageSize) setCurrentPage(currentPage + 1);
                  }}
                  className={wishlists.length < pageSize ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}
